import i18next from "i18next";
import { useTranslation } from "react-i18next";

import {
  PrimaryCareProviderSelection,
  type ProgramOffering,
} from "@/constants";
import { type SurveyAnswers, useAppContext } from "@/contexts";
import { getAge } from "@/utils/age";

import { useTrackEvent } from "./useTrackEvent";

export enum HeaderSectionType {
  WEIGHT,
  DIABETES,
  MED,
}

export enum ValuePropsVariation {
  WEIGHT,
  WEIGHT_MED_COMPANION,
  WEIGHT_DIABETES_SUPPORT,
  WEIGHT_DIABETES_SUPPORT_MED_COMPANION,
  DPP,
  DPP_MED_COMPANION,
  MED,
  MED_DIABETES_SUPPORT,
  NMPBA,
}

type AgeGroupKey = "20s" | "30s" | "40s" | "50s" | "60s" | "70+";
type GenderKey = "male" | "female" | "default";
type PeopleHelpedKey = `${GenderKey}_${AgeGroupKey}`;

type HeaderSectionMapProperties = {
  graphImageUrl: string;
};

export type ProgramResultValueProp = {
  titleKeySuffix: string;
  descriptionKeySuffix: string;
  imageSrc: string;
};

const headerSectionMap: Record<HeaderSectionType, HeaderSectionMapProperties> =
  {
    [HeaderSectionType.WEIGHT]: {
      graphImageUrl: "/assets/img/program-result-header-weight.png",
    },
    [HeaderSectionType.DIABETES]: {
      graphImageUrl: "/assets/img/program-result-header-diabetes.png",
    },
    [HeaderSectionType.MED]: {
      graphImageUrl: "/assets/img/program-result-header-med.png",
    },
  };

const getHeaderSectionType = (
  programOffering: ProgramOffering,
  includeDiabetesSupport: boolean,
) => {
  if (programOffering === "MED" || programOffering === "NMPBA") {
    return HeaderSectionType.MED;
  }

  if (programOffering === "DPP" || includeDiabetesSupport) {
    return HeaderSectionType.DIABETES;
  }

  return HeaderSectionType.WEIGHT;
};

// Buyflow has experiments with 70+, 70s, and 80+, but all with the same count.
// For simplicity, we will just use 70+ for all ages 70 and older.
const PEOPLE_HELPED_MAP: Record<PeopleHelpedKey, number> = {
  default_20s: 331545,
  default_30s: 888958,
  default_40s: 968553,
  default_50s: 820399,
  default_60s: 459883,
  "default_70+": 136886,
  female_20s: 284774,
  female_30s: 744788,
  female_40s: 801508,
  female_50s: 679366,
  female_60s: 382049,
  "female_70+": 111131,
  male_20s: 46425,
  male_30s: 143766,
  male_40s: 166325,
  male_50s: 140820,
  male_60s: 77645,
  "male_70+": 25703,
};

const getSocialProofText = (surveyAnswers: SurveyAnswers) => {
  const { gender } = surveyAnswers;
  const age = getAge({ surveyAnswers });

  let genderKey: GenderKey = "default";
  if (gender === "female") {
    genderKey = "female";
  } else if (gender === "male") {
    genderKey = "male";
  }

  // Since we don't have the number of people helped between 18-20, we are
  // using the 20s age group for all ages below 30
  let ageGroup: AgeGroupKey = "20s";
  if (!age) {
    ageGroup = "20s";
  } else if (age >= 70) {
    ageGroup = "70+";
  } else if (age >= 60) {
    ageGroup = "60s";
  } else if (age >= 50) {
    ageGroup = "50s";
  } else if (age >= 40) {
    ageGroup = "40s";
  } else if (age >= 30) {
    ageGroup = "30s";
  }

  const peopleHelpedKey: PeopleHelpedKey = `${genderKey}_${ageGroup}`;
  const count = PEOPLE_HELPED_MAP[peopleHelpedKey];

  const browserLanguage = window.navigator.languages[0];
  const formattedCount = new Intl.NumberFormat(browserLanguage).format(count);

  const genderTranslationKey =
    ageGroup === "70+" ? `${genderKey}_70+` : genderKey;

  const socialProofText = i18next.t(
    `programResult.descriptionSection.socialProofText.${genderTranslationKey}`,
    {
      ageGroup,
      formattedCount,
    },
  );
  return socialProofText;
};

const getValuePropsVariation = (
  programOffering: ProgramOffering,
  includeGlp1Medication: boolean,
  includeDiabetesSupport: boolean,
) => {
  if (programOffering === "NMPBA") {
    return ValuePropsVariation.NMPBA;
  }

  if (programOffering === "MED") {
    return includeDiabetesSupport
      ? ValuePropsVariation.MED_DIABETES_SUPPORT
      : ValuePropsVariation.MED;
  }

  if (programOffering === "DPP") {
    return includeGlp1Medication
      ? ValuePropsVariation.DPP_MED_COMPANION
      : ValuePropsVariation.DPP;
  }

  if (includeDiabetesSupport && includeGlp1Medication) {
    return ValuePropsVariation.WEIGHT_DIABETES_SUPPORT_MED_COMPANION;
  }

  if (includeDiabetesSupport) {
    return ValuePropsVariation.WEIGHT_DIABETES_SUPPORT;
  }

  if (includeGlp1Medication) {
    return ValuePropsVariation.WEIGHT_MED_COMPANION;
  }

  return ValuePropsVariation.WEIGHT;
};

const getValueProps = (valuePropsVariation: ValuePropsVariation) => {
  const valuePropsVariationMap: Record<
    ValuePropsVariation,
    ProgramResultValueProp[]
  > = {
    [ValuePropsVariation.WEIGHT]: [
      {
        titleKeySuffix: "provenWeightLossProgram",
        descriptionKeySuffix: "provenWeightLossProgram-1",
        imageSrc: "/assets/img/program-result-value-prop-female-1.png",
      },
      {
        titleKeySuffix: "personalizedToolsForSuccess",
        descriptionKeySuffix: "personalizedToolsForSuccess-1",
        imageSrc: "/assets/img/program-result-value-prop-body-1.png",
      },
      {
        titleKeySuffix: "lastingResults",
        descriptionKeySuffix: "lastingResults-1",
        imageSrc: "/assets/img/program-result-value-prop-graph-1.png",
      },
    ],
    [ValuePropsVariation.WEIGHT_MED_COMPANION]: [
      {
        titleKeySuffix: "provenWeightLossProgram",
        descriptionKeySuffix: "provenWeightLossProgram-1",
        imageSrc: "/assets/img/program-result-value-prop-female-1.png",
      },
      {
        titleKeySuffix: "glp1CompanionExperience",
        descriptionKeySuffix: "glp1CompanionExperience",
        imageSrc: "/assets/img/program-result-value-prop-body-2.png",
      },
      {
        titleKeySuffix: "lastingResults",
        descriptionKeySuffix: "lastingResults-2",
        imageSrc: "/assets/img/program-result-value-prop-graph-2.png",
      },
    ],
    [ValuePropsVariation.WEIGHT_DIABETES_SUPPORT]: [
      {
        titleKeySuffix: "provenWeightLossProgram",
        descriptionKeySuffix: "provenWeightLossProgram-1",
        imageSrc: "/assets/img/program-result-value-prop-female-1.png",
      },
      {
        titleKeySuffix: "tailoredDiabetesSupport",
        descriptionKeySuffix: "tailoredDiabetesSupport",
        imageSrc: "/assets/img/program-result-value-prop-glucometer.png",
      },
      {
        titleKeySuffix: "lastingResults",
        descriptionKeySuffix: "lastingResults-3",
        imageSrc: "/assets/img/program-result-value-prop-graph-1.png",
      },
    ],
    [ValuePropsVariation.WEIGHT_DIABETES_SUPPORT_MED_COMPANION]: [
      {
        titleKeySuffix: "tailoredDiabetesSupport",
        descriptionKeySuffix: "tailoredDiabetesSupport",
        imageSrc: "/assets/img/program-result-value-prop-female-2.png",
      },
      {
        titleKeySuffix: "personalizedToolsForSuccess",
        descriptionKeySuffix: "personalizedToolsForSuccess-2",
        imageSrc: "/assets/img/program-result-value-prop-body-2.png",
      },
      {
        titleKeySuffix: "lastingResults",
        descriptionKeySuffix: "lastingResults-4",
        imageSrc: "/assets/img/program-result-value-prop-graph-2.png",
      },
    ],
    [ValuePropsVariation.DPP]: [
      {
        titleKeySuffix: "diabetesPreventionProgram",
        descriptionKeySuffix: "diabetesPreventionProgram",
        imageSrc: "/assets/img/program-result-value-prop-female-2.png",
      },
      {
        titleKeySuffix: "personalizedToolsForSuccess",
        descriptionKeySuffix: "personalizedToolsForSuccess-3",
        imageSrc: "/assets/img/program-result-value-prop-body-1.png",
      },
      {
        titleKeySuffix: "lastingResults",
        descriptionKeySuffix: "lastingResults-5",
        imageSrc: "/assets/img/program-result-value-prop-graph-3.png",
      },
    ],
    [ValuePropsVariation.DPP_MED_COMPANION]: [
      {
        titleKeySuffix: "diabetesPreventionProgram",
        descriptionKeySuffix: "diabetesPreventionProgram",
        imageSrc: "/assets/img/program-result-value-prop-female-2.png",
      },
      {
        titleKeySuffix: "personalizedToolsForSuccess",
        descriptionKeySuffix: "personalizedToolsForSuccess-2",
        imageSrc: "/assets/img/program-result-value-prop-body-2.png",
      },
      {
        titleKeySuffix: "lastingResults",
        descriptionKeySuffix: "lastingResults-2",
        imageSrc: "/assets/img/program-result-value-prop-graph-2.png",
      },
    ],
    [ValuePropsVariation.MED]: [
      {
        titleKeySuffix: "provenWeightLossProgram",
        descriptionKeySuffix: "provenWeightLossProgram-2",
        imageSrc: "/assets/img/program-result-value-prop-female-3.png",
      },
      {
        titleKeySuffix: "unlimitedClinicalCare",
        descriptionKeySuffix: "unlimitedClinicalCare",
        imageSrc: "/assets/img/program-result-value-prop-med.png",
      },
      {
        titleKeySuffix: "lastingResults",
        descriptionKeySuffix: "lastingResults-6",
        imageSrc: "/assets/img/program-result-value-prop-graph-4.png",
      },
    ],
    [ValuePropsVariation.MED_DIABETES_SUPPORT]: [
      {
        titleKeySuffix: "weightLossDiabetesSupport",
        descriptionKeySuffix: "weightLossDiabetesSupport",
        imageSrc: "/assets/img/program-result-value-prop-female-2.png",
      },
      {
        titleKeySuffix: "unlimitedClinicalCare",
        descriptionKeySuffix: "unlimitedClinicalCare",
        imageSrc: "/assets/img/program-result-value-prop-med.png",
      },
      {
        titleKeySuffix: "lastingResults",
        descriptionKeySuffix: "lastingResults-7",
        imageSrc: "/assets/img/program-result-value-prop-graph-4.png",
      },
    ],
    [ValuePropsVariation.NMPBA]: [
      {
        titleKeySuffix: "weightLossDiabetesSupport",
        descriptionKeySuffix: "weightLossDiabetesSupport",
        imageSrc: "/assets/img/program-result-value-prop-female-2.png",
      },
      {
        titleKeySuffix: "unlimitedClinicalCare",
        descriptionKeySuffix: "unlimitedClinicalCare",
        imageSrc: "/assets/img/program-result-value-prop-med.png",
      },
      {
        titleKeySuffix: "lastingResults",
        descriptionKeySuffix: "lastingResults-7",
        imageSrc: "/assets/img/program-result-value-prop-graph-4.png",
      },
    ],
  };

  const valueProps = valuePropsVariationMap[valuePropsVariation];
  return valueProps;
};

export const useProgramResult = () => {
  const { partnerInfo, selectedProgram, surveyAnswers } = useAppContext();
  const { trackAnonEvent } = useTrackEvent();
  const { t } = useTranslation();

  const {
    glp1Medication,
    heightWeight: { weight } = {},
    idealWeight,
    primaryCareProvider,
  } = surveyAnswers;

  const includeDiabetesSupport =
    primaryCareProvider === PrimaryCareProviderSelection.YES;
  const includeGlp1Medication =
    glp1Medication === "yes" || glp1Medication === "prescriptionNotYetStarted";
  const isAccolade = partnerInfo?.configs?.ACCOLADE_PARTNER || false;

  const programOffering = selectedProgram ?? (isAccolade ? "NMPBA" : "MED");

  const headerSectionType = getHeaderSectionType(
    programOffering,
    includeDiabetesSupport,
  );
  const { graphImageUrl } = headerSectionMap[headerSectionType];

  const personalizedProgramTextKey = `programResult.headerSection.personalizedProgramText.${programOffering?.toLowerCase()}`;

  const socialProofText = getSocialProofText(surveyAnswers);

  const valuePropsVariation = getValuePropsVariation(
    programOffering,
    includeGlp1Medication,
    includeDiabetesSupport,
  );
  const valueProps = getValueProps(valuePropsVariation);

  const weightLossTooltipHeadingText = t(
    "programResult.headerSection.weightLossTooltip.heading",
  );

  const weightLossTooltipWeightText =
    weight !== undefined &&
    idealWeight !== undefined &&
    t("programResult.headerSection.weightLossTooltip.weight", {
      weight: idealWeight,
    });

  const trackProgramResultPageViewedEvent = () => {
    const coursePacks = [
      ...(includeDiabetesSupport ? ["DIABETES_SUPPORT"] : []),
      ...(includeGlp1Medication ? ["GLP1"] : []),
    ];

    trackAnonEvent("b2bEnrollmentAnonProgramResultPageViewed", {
      b2bProgram: programOffering,
      coursePacks,
    });
  };

  return {
    graphImageUrl,
    headerSectionType,
    personalizedProgramTextKey,
    socialProofText,
    trackProgramResultPageViewedEvent,
    valueProps,
    valuePropsVariation,
    weightLossTooltipHeadingText,
    weightLossTooltipWeightText,
  };
};
